// src/pages/expert/AdvisorQuoteEditor.jsx
// §7bis — Écran « Préparer le devis ». Prépare le dossier prêt à deviser puis
// prêt à facturer. ARTKIV ne fixe PAS les honoraires (montants entièrement
// éditables) et n'émet PAS de facture électronique réglementaire : le numéro
// interne D-AAAA-xxxx est un brouillon, la facture finale est émise par l'outil
// de l'Advisor. i18n FR/EN/DE inline.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { missionTypeLabel } from "../../config/advisorMissions";
import { downloadAdvisorQuotePdf } from "../../lib/advisorQuoteGenerator";
import { buildQuoteExport, downloadQuoteExport } from "../../lib/advisorQuoteExport";
import { Plus, Trash2, ArrowLeft, Save, Send, CheckCircle2, FileDown, FileJson } from "lucide-react";

/* ── Copie FR / EN / DE ───────────────────────────────────── */
const COPY = {
  fr: {
    back: "Retour", title: "Préparer le devis", missionLabel: "Mission",
    draftNo: "N° interne", statusLabel: "Statut",
    statuses: { draft: "Brouillon", sent: "Envoyé", accepted: "Accepté", refused: "Refusé", expired: "Expiré" },
    object: "Objet du devis", objectPh: "Ex. : Audit documentaire et revalorisation de la collection…",
    clientIdentity: "Identité de facturation du client",
    clientType: "Type", individual: "Particulier", company: "Professionnel",
    zone: "Zone", fr: "France", eu: "Union européenne", nonEu: "Hors UE",
    name: "Nom / raison sociale", email: "Email de facturation",
    address: "Adresse", siren: "SIREN / SIRET", vat: "N° TVA intracom.", poRef: "Réf. / bon de commande",
    lines: "Prestations et débours", addLine: "Ajouter une ligne", addExpense: "Ajouter un débours",
    colDesignation: "Désignation", colUnit: "Unité", colQty: "Qté", colPrice: "PU HT",
    colDiscount: "Remise %", colVat: "TVA %", colTotal: "Total HT", colDel: "",
    units: { flat: "Forfait", day: "Jour", hour: "Heure", artwork: "Œuvre", percentage: "%", other: "Autre" },
    designationPh: "Désignation de la prestation",
    globalDiscount: "Remise globale HT", deposit: "Acompte",
    subtotal: "Sous-total HT", afterDiscount: "HT après remise", tax: "TVA", total: "Total TTC",
    validUntil: "Validité du devis", delivery: "Délai / réalisation", payment: "Conditions de règlement",
    notes: "Notes", exclusions: "Exclusions",
    save: "Enregistrer le brouillon", genPdf: "Générer l’aperçu PDF", exportJson: "Exporter vers mon outil", markSent: "Marquer envoyé", markAccepted: "Marquer accepté",
    saved: "Devis enregistré.", saveError: "Enregistrement impossible.",
    disclaimer: "ARTKIV prépare et organise le devis. Vous fixez vos honoraires, la TVA et les mentions ; la facture définitive est émise par votre outil de facturation. Ce document n'est pas une facture électronique réglementaire.",
    loading: "Chargement…", missingMission: "Mission introuvable.",
    emptyLine: "Aucune ligne. Ajoutez une prestation pour commencer.",
    deliveryPh: "Ex. : 15 jours après réception des documents",
    paymentPh: "Ex. : 30 jours à réception de facture",
  },
  en: {
    back: "Back", title: "Prepare the quote", missionLabel: "Mission",
    draftNo: "Internal no.", statusLabel: "Status",
    statuses: { draft: "Draft", sent: "Sent", accepted: "Accepted", refused: "Refused", expired: "Expired" },
    object: "Quote object", objectPh: "E.g.: Documentary audit and revaluation of the collection…",
    clientIdentity: "Client billing identity",
    clientType: "Type", individual: "Individual", company: "Business",
    zone: "Zone", fr: "France", eu: "European Union", nonEu: "Outside EU",
    name: "Name / company", email: "Billing email",
    address: "Address", siren: "SIREN / SIRET", vat: "Intra-EU VAT no.", poRef: "Ref. / purchase order",
    lines: "Services and expenses", addLine: "Add a line", addExpense: "Add an expense",
    colDesignation: "Description", colUnit: "Unit", colQty: "Qty", colPrice: "Unit price",
    colDiscount: "Disc. %", colVat: "VAT %", colTotal: "Total excl.", colDel: "",
    units: { flat: "Flat", day: "Day", hour: "Hour", artwork: "Artwork", percentage: "%", other: "Other" },
    designationPh: "Service description",
    globalDiscount: "Global discount (excl.)", deposit: "Deposit",
    subtotal: "Subtotal excl.", afterDiscount: "After discount", tax: "VAT", total: "Total incl.",
    validUntil: "Quote validity", delivery: "Lead time / delivery", payment: "Payment terms",
    notes: "Notes", exclusions: "Exclusions",
    save: "Save draft", genPdf: "Generate PDF preview", exportJson: "Export to my tool", markSent: "Mark as sent", markAccepted: "Mark as accepted",
    saved: "Quote saved.", saveError: "Could not save.",
    disclaimer: "ARTKIV prepares and organises the quote. You set your fees, VAT and mentions; the final invoice is issued by your billing tool. This document is not a regulatory electronic invoice.",
    loading: "Loading…", missingMission: "Mission not found.",
    emptyLine: "No line yet. Add a service to start.",
    deliveryPh: "E.g.: 15 days after receiving the documents",
    paymentPh: "E.g.: 30 days on receipt of invoice",
  },
  de: {
    back: "Zurück", title: "Angebot vorbereiten", missionLabel: "Auftrag",
    draftNo: "Interne Nr.", statusLabel: "Status",
    statuses: { draft: "Entwurf", sent: "Gesendet", accepted: "Angenommen", refused: "Abgelehnt", expired: "Abgelaufen" },
    object: "Gegenstand des Angebots", objectPh: "Z. B.: Dokumentenprüfung und Neubewertung der Sammlung…",
    clientIdentity: "Rechnungsidentität des Kunden",
    clientType: "Typ", individual: "Privatperson", company: "Unternehmen",
    zone: "Zone", fr: "Frankreich", eu: "Europäische Union", nonEu: "Außerhalb der EU",
    name: "Name / Firma", email: "Rechnungs-E-Mail",
    address: "Adresse", siren: "SIREN / SIRET", vat: "USt-IdNr.", poRef: "Ref. / Bestellnr.",
    lines: "Leistungen und Auslagen", addLine: "Zeile hinzufügen", addExpense: "Auslage hinzufügen",
    colDesignation: "Bezeichnung", colUnit: "Einheit", colQty: "Menge", colPrice: "EP netto",
    colDiscount: "Rabatt %", colVat: "USt %", colTotal: "Netto", colDel: "",
    units: { flat: "Pauschal", day: "Tag", hour: "Stunde", artwork: "Werk", percentage: "%", other: "Andere" },
    designationPh: "Leistungsbeschreibung",
    globalDiscount: "Gesamtrabatt (netto)", deposit: "Anzahlung",
    subtotal: "Zwischensumme netto", afterDiscount: "Nach Rabatt", tax: "USt", total: "Gesamt brutto",
    validUntil: "Gültigkeit", delivery: "Frist / Lieferung", payment: "Zahlungsbedingungen",
    notes: "Notizen", exclusions: "Ausschlüsse",
    save: "Entwurf speichern", genPdf: "PDF-Vorschau erstellen", exportJson: "In mein Tool exportieren", markSent: "Als gesendet markieren", markAccepted: "Als angenommen markieren",
    saved: "Angebot gespeichert.", saveError: "Speichern nicht möglich.",
    disclaimer: "ARTKIV bereitet das Angebot auf. Sie legen Honorare, USt und Hinweise fest; die endgültige Rechnung erstellt Ihr Abrechnungstool. Dieses Dokument ist keine gesetzliche elektronische Rechnung.",
    loading: "Laden…", missingMission: "Auftrag nicht gefunden.",
    emptyLine: "Noch keine Zeile. Fügen Sie eine Leistung hinzu.",
    deliveryPh: "Z. B.: 15 Tage nach Erhalt der Unterlagen",
    paymentPh: "Z. B.: 30 Tage nach Rechnungserhalt",
  },
};
const pick = (lang) => COPY[String(lang || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

// Assistant de chiffrage : lignes de départ selon le type de mission (éditables).
const STARTER = {
  fr: {
    valuation: ["Analyse et mise à jour des fiches œuvres", "Recherche de comparables et fourchettes de valeur", "Rapport de synthèse"],
    inventory: ["Inventaire et structuration des données", "Photographies et identification", "Rapport d’inventaire"],
    provenance_research: ["Recherche documentaire de provenance", "Rédaction de la note de provenance"],
    condition_review: ["Constat d’état des œuvres", "Rapport de conservation"],
    insurance_file: ["Constitution du dossier d’assurance", "Estimation des valeurs assurées"],
    curatorial_report: ["Rapport patrimonial de synthèse"],
    _default: ["Prestation de conseil"],
  },
  en: {
    valuation: ["Analysis and update of artwork records", "Comparables research and value ranges", "Synthesis report"],
    inventory: ["Inventory and data structuring", "Photography and identification", "Inventory report"],
    provenance_research: ["Provenance documentary research", "Provenance note drafting"],
    condition_review: ["Condition report of the works", "Conservation report"],
    insurance_file: ["Insurance file preparation", "Insured value estimation"],
    curatorial_report: ["Synthetic patrimonial report"],
    _default: ["Advisory service"],
  },
  de: {
    valuation: ["Analyse und Aktualisierung der Werkakten", "Vergleichsrecherche und Wertspannen", "Syntheseberichte"],
    inventory: ["Inventar und Datenstrukturierung", "Fotografie und Identifikation", "Inventarbericht"],
    provenance_research: ["Provenienzrecherche", "Erstellung der Provenienznotiz"],
    condition_review: ["Zustandsbericht der Werke", "Konservierungsbericht"],
    insurance_file: ["Erstellung der Versicherungsakte", "Schätzung der Versicherungswerte"],
    curatorial_report: ["Zusammenfassender Vermögensbericht"],
    _default: ["Beratungsleistung"],
  },
};

const localeOf = (l) => (String(l).slice(0, 2) === "de" ? "de-DE" : String(l).slice(0, 2) === "en" ? "en-GB" : "fr-FR");
const money = (n, l) => (Number(n) || 0).toLocaleString(localeOf(l), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v)) || 0;

const emptyLine = (kind = "service") => ({
  _key: Math.random().toString(36).slice(2),
  kind, label: "", detail: "", unit: kind === "expense" ? "flat" : "flat",
  qty: 1, unit_price_ex_tax: 0, discount_pct: 0, vat_rate: 20,
});

export default function AdvisorQuoteEditor() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || "fr";
  const tx = pick(lang);

  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(null);
  const [quoteId, setQuoteId] = useState(null);
  const [internalNumber, setInternalNumber] = useState(null);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);

  const [object, setObject] = useState("");
  const [client, setClient] = useState({ type: "company", zone: "fr", name: "", email: "", address: "", siren: "", vat: "", po_ref: "" });
  const [lines, setLines] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [deposit, setDeposit] = useState(0);
  const [validUntil, setValidUntil] = useState("");
  const [delivery, setDelivery] = useState("");
  const [payment, setPayment] = useState("");
  const [notes, setNotes] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [defaultVat, setDefaultVat] = useState(20);
  const [vatMention, setVatMention] = useState("");
  const [advisorProfile, setAdvisorProfile] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.id || !missionId) { setLoading(false); return; }
      setLoading(true);
      const [{ data: m }, { data: prof }, { data: existing }] = await Promise.all([
        supabase.from("advisor_missions").select("*").eq("id", missionId).eq("advisor_id", user.id).maybeSingle(),
        supabase.from("advisor_billing_profiles").select("*").eq("advisor_id", user.id).maybeSingle(),
        supabase.from("advisor_quotes").select("*").eq("mission_id", missionId).eq("advisor_id", user.id).order("created_at", { ascending: false }).limit(1).maybeSingle(),
      ]);
      if (!alive) return;
      setMission(m || null);
      setAdvisorProfile(prof || null);

      const vatRate = prof?.vat_regime === "franchise_base" ? 0 : (prof?.default_vat_rate != null ? Number(prof.default_vat_rate) : 20);
      setDefaultVat(vatRate);
      setVatMention(prof?.vat_exemption_mention || "");

      if (existing) {
        setQuoteId(existing.id);
        setInternalNumber(existing.internal_number);
        setStatus(existing.status || "draft");
        setObject(existing.object || "");
        setClient({ type: "company", zone: "fr", name: "", email: "", address: "", siren: "", vat: "", po_ref: "", ...(existing.client_billing_identity || {}) });
        setDiscountAmount(Number(existing.discount_amount) || 0);
        setDeposit(Number(existing.deposit_amount) || 0);
        setValidUntil(existing.valid_until || "");
        setDelivery(existing.delivery_terms || "");
        setPayment(existing.payment_terms || "");
        setNotes(existing.notes || "");
        setExclusions(existing.exclusions || "");
        const { data: qlines } = await supabase.from("advisor_quote_lines").select("*").eq("quote_id", existing.id).order("sort_order");
        setLines((qlines || []).map((l) => ({ _key: l.id, id: l.id, ...l })));
      } else {
        // Nouveau devis : assistant de chiffrage prérempli depuis le type de mission.
        setObject(m?.title || "");
        const starters = (STARTER[String(lang).slice(0, 2)] || STARTER.fr);
        const labels = starters[m?.mission_type] || starters._default;
        setLines(labels.map((label) => ({ ...emptyLine(), label, vat_rate: vatRate })));
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [user?.id, missionId, lang]);

  const totals = useMemo(() => {
    let baseHT = 0, taxRaw = 0;
    for (const l of lines) {
      const net = num(l.qty) * num(l.unit_price_ex_tax) * (1 - num(l.discount_pct) / 100);
      baseHT += net;
      taxRaw += net * num(l.vat_rate) / 100;
    }
    const afterDiscount = Math.max(0, baseHT - num(discountAmount));
    const scale = baseHT > 0 ? afterDiscount / baseHT : 0;
    const tax = taxRaw * scale;
    return { baseHT, afterDiscount, tax, total: afterDiscount + tax };
  }, [lines, discountAmount]);

  const setLine = (key, patch) => setLines((prev) => prev.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  const addLine = (kind) => setLines((prev) => [...prev, { ...emptyLine(kind), vat_rate: defaultVat }]);
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l._key !== key));

  const persist = useCallback(async (nextStatus) => {
    if (!user?.id || !mission) return null;
    setSaving(true);
    try {
      const payload = {
        advisor_id: user.id,
        mission_id: mission.id,
        client_id: mission.client_id || null,
        collection_id: mission.collection_id || null,
        // Rattachement au dossier client (§8bis) hérité de la mission.
        client_entity_id: mission.client_entity_id || null,
        status: nextStatus || status,
        object: object.trim() || null,
        client_billing_identity: client,
        tax_context: { default_vat_rate: defaultVat, exemption_mention: vatMention || null, zone: client.zone },
        discount_amount: num(discountAmount),
        deposit_amount: num(deposit),
        subtotal_ex_tax: Math.round(totals.afterDiscount * 100) / 100,
        tax_amount: Math.round(totals.tax * 100) / 100,
        total_inc_tax: Math.round(totals.total * 100) / 100,
        valid_until: validUntil || null,
        delivery_terms: delivery.trim() || null,
        payment_terms: payment.trim() || null,
        notes: notes.trim() || null,
        exclusions: exclusions.trim() || null,
        updated_at: new Date().toISOString(),
        created_by: user.id,
        ...(nextStatus === "sent" ? { sent_at: new Date().toISOString() } : {}),
        ...(nextStatus === "accepted" ? { accepted_at: new Date().toISOString() } : {}),
      };

      let qid = quoteId;
      if (qid) {
        const { error } = await supabase.from("advisor_quotes").update(payload).eq("id", qid);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("advisor_quotes").insert(payload).select("id,internal_number").single();
        if (error) throw error;
        qid = data.id;
        setQuoteId(qid);
        setInternalNumber(data.internal_number);
      }

      // Lignes : on remplace l'ensemble (simple et fiable pour V1).
      await supabase.from("advisor_quote_lines").delete().eq("quote_id", qid);
      if (lines.length) {
        const rows = lines.map((l, i) => ({
          quote_id: qid, sort_order: i, kind: l.kind || "service",
          label: (l.label || "").slice(0, 300), detail: l.detail || null,
          unit: l.unit || "flat", qty: num(l.qty), unit_price_ex_tax: num(l.unit_price_ex_tax),
          discount_pct: num(l.discount_pct), vat_rate: num(l.vat_rate),
        }));
        const { error: le } = await supabase.from("advisor_quote_lines").insert(rows);
        if (le) throw le;
      }

      // §7bis : refléter l'état commercial du devis sur la mission (avance douce,
      // uniquement depuis les statuts amont, jamais de régression).
      if (nextStatus === "sent" && mission.status === "draft") {
        await supabase.from("advisor_missions").update({ status: "proposed" }).eq("id", mission.id);
      } else if (nextStatus === "accepted" && ["draft", "proposed"].includes(mission.status)) {
        await supabase.from("advisor_missions").update({ status: "accepted" }).eq("id", mission.id);
      }

      if (nextStatus) setStatus(nextStatus);
      return qid;
    } catch (e) {
      console.warn("[AdvisorQuoteEditor] persist", e);
      alert(`${tx.saveError} ${e?.message || ""}`);
      return null;
    } finally {
      setSaving(false);
    }
  }, [user?.id, mission, status, object, client, defaultVat, vatMention, discountAmount, deposit, totals, validUntil, delivery, payment, notes, exclusions, quoteId, lines, tx]);

  // Aperçu PDF : on enregistre d'abord (numéro + cohérence), puis on génère.
  const handlePdf = useCallback(async () => {
    const qid = await persist();
    if (!qid) return;
    let number = internalNumber;
    if (!number) {
      const { data } = await supabase.from("advisor_quotes").select("internal_number").eq("id", qid).maybeSingle();
      number = data?.internal_number || null;
      if (number) setInternalNumber(number);
    }
    await downloadAdvisorQuotePdf({
      internalNumber: number, status, object,
      advisor: advisorProfile, client, lines, totals,
      discountAmount, deposit, validUntil, delivery, payment, notes, exclusions, lang,
    });
  }, [persist, internalNumber, status, object, advisorProfile, client, lines, totals, discountAmount, deposit, validUntil, delivery, payment, notes, exclusions, lang]);

  // Export stable JSON (§7bis) : enregistre puis produit la structure d'import
  // pour Pennylane / l'outil de facturation de l'Advisor.
  const handleExport = useCallback(async () => {
    const qid = await persist();
    if (!qid) return;
    let number = internalNumber;
    if (!number) {
      const { data } = await supabase.from("advisor_quotes").select("internal_number").eq("id", qid).maybeSingle();
      number = data?.internal_number || null;
      if (number) setInternalNumber(number);
    }
    const payload = buildQuoteExport({
      quote: {
        internal_number: number, status, object, currency: "EUR",
        valid_until: validUntil || null, delivery_terms: delivery || null,
        payment_terms: payment || null, notes: notes || null, exclusions: exclusions || null,
        discount_amount: discountAmount, deposit_amount: deposit,
      },
      lines, advisorProfile, mission, client, totals,
    });
    downloadQuoteExport(payload, number);
  }, [persist, internalNumber, status, object, validUntil, delivery, payment, notes, exclusions, discountAmount, deposit, lines, advisorProfile, mission, client, totals]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#f9f7f4] text-gray-400">{tx.loading}</div>;
  if (!mission) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#f9f7f4] text-gray-500">
      {tx.missingMission}
      <button onClick={() => navigate("/expert/dashboard?tab=missions")} className="rounded-lg bg-gray-900 px-4 py-2 text-sm text-white">{tx.back}</button>
    </div>
  );

  const setC = (k, v) => setClient((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-[#f9f7f4] px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <button onClick={() => navigate("/expert/dashboard?tab=missions")} className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft size={16} /> {tx.back}
        </button>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl text-gray-900" style={{ fontFamily: "Georgia, serif" }}>{tx.title}</h1>
            <p className="mt-1 text-sm text-gray-500">{tx.missionLabel} · {mission.title} · {missionTypeLabel(mission.mission_type, lang)}</p>
          </div>
          <div className="text-right text-sm">
            {internalNumber && <div className="text-gray-500">{tx.draftNo} <span className="font-medium text-gray-800">{internalNumber}</span></div>}
            <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1">
              <span className="text-gray-400">{tx.statusLabel}</span>
              <span className="font-medium text-gray-800">{tx.statuses[status]}</span>
            </div>
          </div>
        </div>

        {/* Objet */}
        <Card>
          <Label>{tx.object}</Label>
          <input className="qe-in" value={object} onChange={(e) => setObject(e.target.value)} placeholder={tx.objectPh} />
        </Card>

        {/* Identité de facturation client */}
        <Card title={tx.clientIdentity}>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={tx.clientType}>
              <select className="qe-in" value={client.type} onChange={(e) => setC("type", e.target.value)}>
                <option value="individual">{tx.individual}</option>
                <option value="company">{tx.company}</option>
              </select>
            </Field>
            <Field label={tx.zone}>
              <select className="qe-in" value={client.zone} onChange={(e) => setC("zone", e.target.value)}>
                <option value="fr">{tx.fr}</option>
                <option value="eu">{tx.eu}</option>
                <option value="non_eu">{tx.nonEu}</option>
              </select>
            </Field>
            <Field label={tx.name}><input className="qe-in" value={client.name} onChange={(e) => setC("name", e.target.value)} /></Field>
            <Field label={tx.email}><input className="qe-in" value={client.email} onChange={(e) => setC("email", e.target.value)} /></Field>
            <Field label={tx.address}><input className="qe-in" value={client.address} onChange={(e) => setC("address", e.target.value)} /></Field>
            {client.type === "company" && (
              <>
                <Field label={tx.siren}><input className="qe-in" value={client.siren} onChange={(e) => setC("siren", e.target.value)} /></Field>
                <Field label={tx.vat}><input className="qe-in" value={client.vat} onChange={(e) => setC("vat", e.target.value)} /></Field>
              </>
            )}
            <Field label={tx.poRef}><input className="qe-in" value={client.po_ref} onChange={(e) => setC("po_ref", e.target.value)} /></Field>
          </div>
        </Card>

        {/* Lignes */}
        <Card title={tx.lines}>
          {lines.length === 0 ? (
            <p className="py-4 text-sm text-gray-400">{tx.emptyLine}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                  <tr>
                    <th className="py-2 pr-2">{tx.colDesignation}</th>
                    <th className="py-2 px-2">{tx.colUnit}</th>
                    <th className="py-2 px-2 w-16">{tx.colQty}</th>
                    <th className="py-2 px-2 w-24">{tx.colPrice}</th>
                    <th className="py-2 px-2 w-20">{tx.colDiscount}</th>
                    <th className="py-2 px-2 w-20">{tx.colVat}</th>
                    <th className="py-2 px-2 text-right w-28">{tx.colTotal}</th>
                    <th className="py-2 pl-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const net = num(l.qty) * num(l.unit_price_ex_tax) * (1 - num(l.discount_pct) / 100);
                    return (
                      <tr key={l._key} className="border-t border-gray-100 align-top">
                        <td className="py-2 pr-2">
                          <input className="qe-in" value={l.label} onChange={(e) => setLine(l._key, { label: e.target.value })} placeholder={tx.designationPh} />
                          {l.kind === "expense" && <span className="mt-1 inline-block text-[10px] uppercase tracking-wide text-amber-700">{tx.addExpense}</span>}
                        </td>
                        <td className="py-2 px-2">
                          <select className="qe-in" value={l.unit} onChange={(e) => setLine(l._key, { unit: e.target.value })}>
                            {Object.entries(tx.units).map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                          </select>
                        </td>
                        <td className="py-2 px-2"><input type="number" className="qe-in" value={l.qty} onChange={(e) => setLine(l._key, { qty: e.target.value })} /></td>
                        <td className="py-2 px-2"><input type="number" className="qe-in" value={l.unit_price_ex_tax} onChange={(e) => setLine(l._key, { unit_price_ex_tax: e.target.value })} /></td>
                        <td className="py-2 px-2"><input type="number" className="qe-in" value={l.discount_pct} onChange={(e) => setLine(l._key, { discount_pct: e.target.value })} /></td>
                        <td className="py-2 px-2"><input type="number" className="qe-in" value={l.vat_rate} onChange={(e) => setLine(l._key, { vat_rate: e.target.value })} /></td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-700">{money(net, lang)}</td>
                        <td className="py-2 pl-2">
                          <button type="button" onClick={() => removeLine(l._key)} className="text-gray-300 hover:text-rose-600"><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex gap-3">
            <button type="button" onClick={() => addLine("service")} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"><Plus size={15} /> {tx.addLine}</button>
            <button type="button" onClick={() => addLine("expense")} className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-900"><Plus size={15} /> {tx.addExpense}</button>
          </div>
        </Card>

        {/* Totaux + remise + acompte */}
        <Card>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <Field label={tx.globalDiscount}><input type="number" className="qe-in" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} /></Field>
              <Field label={tx.deposit}><input type="number" className="qe-in" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></Field>
              <Field label={tx.validUntil}><input type="date" className="qe-in" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 text-sm">
              <Row k={tx.subtotal} v={money(totals.baseHT, lang)} />
              {num(discountAmount) > 0 && <Row k={`${tx.afterDiscount}`} v={money(totals.afterDiscount, lang)} />}
              <Row k={tx.tax} v={money(totals.tax, lang)} />
              <div className="my-2 border-t border-gray-200" />
              <Row k={<span className="font-semibold text-gray-900">{tx.total}</span>} v={<span className="font-semibold text-gray-900">{money(totals.total, lang)}</span>} />
              {num(deposit) > 0 && <Row k={tx.deposit} v={money(deposit, lang)} />}
            </div>
          </div>
        </Card>

        {/* Conditions / notes */}
        <Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={tx.delivery}><input className="qe-in" value={delivery} onChange={(e) => setDelivery(e.target.value)} placeholder={tx.deliveryPh} /></Field>
            <Field label={tx.payment}><input className="qe-in" value={payment} onChange={(e) => setPayment(e.target.value)} placeholder={tx.paymentPh} /></Field>
            <Field label={tx.notes}><textarea className="qe-in" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
            <Field label={tx.exclusions}><textarea className="qe-in" rows={2} value={exclusions} onChange={(e) => setExclusions(e.target.value)} /></Field>
          </div>
        </Card>

        <p className="mb-4 text-xs leading-relaxed text-gray-400">{tx.disclaimer}</p>

        <div className="sticky bottom-4 flex flex-wrap gap-3 rounded-2xl border border-gray-200 bg-white/95 p-4 shadow-sm backdrop-blur">
          <button type="button" disabled={saving} onClick={() => persist()} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60">
            <Save size={16} /> {tx.save}
          </button>
          <button type="button" disabled={saving} onClick={handlePdf} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60">
            <FileDown size={16} /> {tx.genPdf}
          </button>
          <button type="button" disabled={saving} onClick={handleExport} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60">
            <FileJson size={16} /> {tx.exportJson}
          </button>
          <button type="button" disabled={saving} onClick={() => persist("sent")} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60">
            <Send size={16} /> {tx.markSent}
          </button>
          <button type="button" disabled={saving} onClick={() => persist("accepted")} className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-5 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">
            <CheckCircle2 size={16} /> {tx.markAccepted}
          </button>
        </div>
      </div>

      <style>{`.qe-in{width:100%;border:1px solid #d6d0c4;border-radius:8px;padding:8px 10px;font-size:14px;background:#fff}.qe-in:focus{outline:none;border-color:#c9a961}`}</style>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-5">
      {title && <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">{title}</h2>}
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{label}</label>
      {children}
    </div>
  );
}
function Label({ children }) {
  return <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">{children}</label>;
}
function Row({ k, v }) {
  return <div className="flex items-center justify-between py-1"><span className="text-gray-500">{k}</span><span className="tabular-nums text-gray-700">{v}</span></div>;
}
